import { Controller, useForm } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { Select } from "ui/select";
import { TextField } from "ui/text-field";
import toast from "components/partials/toast/toast";
import {
  PROGRAM_TYPE_OPTIONS,
  type ProgramTypeOption,
} from "containers/admin/clients/client/campaign-templates-page/campaign-template.constants";
import { useCreateCampaignTemplate } from "hooks/api/use-campaign-templates";

import type { CampaignTemplateWithDataDTO } from "generated/schemas";

interface CreateCampaignTemplateModalProps {
  onClose: () => void;
  onCreated: (template: CampaignTemplateWithDataDTO) => void;
}

type CreateCampaignTemplateValues = {
  programType: ProgramTypeOption;
  mondayProgramStrategyUrl: string;
  templateName: string;
};

const isProgramTypeOption = (v: unknown): v is ProgramTypeOption =>
  !!v && typeof v === "object" && !Array.isArray(v) && "value" in v && "label" in v;

const isMondayUrl = (url: string) => url.trim().startsWith("https://");

const buildPayload = ({
  programType,
  mondayProgramStrategyUrl,
  templateName,
}: CreateCampaignTemplateValues) => ({
  name: templateName.trim(),
  programType: programType.value,
  strategy: mondayProgramStrategyUrl.trim(),
  data: {
    audience: {},
    "campaign-duration": {},
    "campaign-details": {
      audienceSegments: [],
      campaignObjective: "APPLICATION_GENERATION",
    },
    creatives: {},
    channels: {},
    "creative-details": {},
    "production-schedule": {},
    "print-details": {},
    "digital-media-details": {},
    "execution-details": {},
    "analysis-considerations": {},
    resources: {},
    "sign-offs": {},
  },
});

const CreateCampaignTemplateModal = ({ onClose, onCreated }: CreateCampaignTemplateModalProps) => {
  const createTemplateMutation = useCreateCampaignTemplate();
  const form = useForm<CreateCampaignTemplateValues>({
    mode: "onChange",
    defaultValues: {
      programType: PROGRAM_TYPE_OPTIONS[0],
      mondayProgramStrategyUrl: "",
      templateName: "",
    },
  });

  const [mondayProgramStrategyUrl, templateName] = form.watch([
    "mondayProgramStrategyUrl",
    "templateName",
  ]);
  const isMondayUrlValid = isMondayUrl(mondayProgramStrategyUrl);
  const isFormValid = isMondayUrlValid && !!templateName.trim();

  const handleSubmit = (values: CreateCampaignTemplateValues) => {
    createTemplateMutation.mutate(buildPayload(values), {
      onSuccess: (template) => {
        toast.success({ data: { title: "Campaign brief created" } });
        onCreated(template);
      },
      onError: () => {
        toast.error({
          data: {
            title: "Could not create campaign brief",
            message: "Please check the details and try again.",
          },
        });
      },
    });
  };

  return (
    <FormDialog
      open
      onClose={onClose}
      closable={!createTemplateMutation.isPending}
      headline="Create campaign brief"
      form={form}
      onSubmit={handleSubmit}
      submitDisabled={!isFormValid}
      submitting={createTemplateMutation.isPending}
      submitButtonText="Create brief"
      submitLoadingMessage="Creating brief...">
      <Controller
        name="programType"
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <Select
            selectName="programType"
            disableClearable
            label="Program type"
            ariaLabel="Select program type"
            required
            options={PROGRAM_TYPE_OPTIONS}
            value={value}
            onChange={(option) => isProgramTypeOption(option) && onChange(option)}
          />
        )}
      />

      <Controller
        name="mondayProgramStrategyUrl"
        control={form.control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Monday program strategy URL"
            required
            helperText={
              field.value && !isMondayUrlValid
                ? undefined
                : "Please include full address starting with https://."
            }
            errMessage={
              field.value && !isMondayUrlValid ? "URL must start with https://" : undefined
            }
          />
        )}
      />

      <Controller
        name="templateName"
        control={form.control}
        render={({ field }) => <TextField {...field} label="Brief name" required />}
      />
    </FormDialog>
  );
};

export default CreateCampaignTemplateModal;
